/**
 * Money with a currency on it.
 *
 * A bare "450" on a rate table is not a rate — it is a number that happens to
 * be next to the word "hour". The product is INR throughout, but a project
 * billed to an overseas client is not, and the rate screen is exactly where
 * that difference decides what somebody invoices.
 */

/** The org's currency where nothing more specific is known. */
export const DEFAULT_CURRENCY = "INR";

const SYMBOLS = {
  INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ",
  AUD: "A$", CAD: "C$", SGD: "S$", JPY: "¥", CHF: "CHF", ZAR: "R", GHS: "₵",
};

/**
 * The currency a country is most likely to pay in.
 *
 * A mirror of COUNTRY_CURRENCY in the backend's utils/money.js, kept small on
 * purpose: this only prefills a field somebody can change. The server derives
 * it again on write and its answer is the one that is stored, so the two
 * drifting costs a wrong suggestion, never a wrong amount.
 */
const COUNTRY_CURRENCY = {
  INDIA: "INR", IN: "INR",
  "UNITED STATES": "USD", USA: "USD", US: "USD",
  "UNITED KINGDOM": "GBP", UK: "GBP", GB: "GBP",
  SINGAPORE: "SGD", SG: "SGD",
  "UNITED ARAB EMIRATES": "AED", UAE: "AED", AE: "AED",
  AUSTRALIA: "AUD", AU: "AUD",
  CANADA: "CAD", CA: "CAD",
  GERMANY: "EUR", FRANCE: "EUR", NETHERLANDS: "EUR", SPAIN: "EUR", IRELAND: "EUR",
  JAPAN: "JPY", JP: "JPY",
};

export const currencyForCountry = (country) =>
  COUNTRY_CURRENCY[String(country || "").trim().toUpperCase()] || DEFAULT_CURRENCY;

export const currencySymbol = (code) =>
  SYMBOLS[String(code || "").toUpperCase()] || String(code || DEFAULT_CURRENCY).toUpperCase();

/**
 * A rate, with its symbol.
 *
 * Two decimals: an hourly rate of 450.5 is a real thing, and rounding it in the
 * table would disagree with the field it was typed into.
 */
export const formatRate = (amount, code = DEFAULT_CURRENCY) => {
  const n = Number(amount);
  if (amount === null || amount === undefined || amount === "" || Number.isNaN(n)) return "—";
  return `${currencySymbol(code)}${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Which currency a rate is in.
 *
 * A rate tied to a project inherits that project's client's currency; anything
 * else is the organisation's own. A rate with no project applies everywhere, so
 * there is no client to ask.
 */
export const currencyForProject = (projectId, projects = []) => {
  if (!projectId) return DEFAULT_CURRENCY;
  const id = String(projectId?._id || projectId);
  const project = (projects || []).find((p) => String(p._id) === id);
  return project?.client?.currency || DEFAULT_CURRENCY;
};
